import Logo from "../../../images/Logo.png";
import { useNavigate } from "react-router-dom";
import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { useAuth } from "../../../context/AuthContext";
import { toast } from '../common/Toast/toast';
import { User, Users, Download, AlertCircle, RefreshCw, BookOpen, Award, Building2, CreditCard, FileText } from "lucide-react";
import { getMe } from "../../../services/AuthService";
import { getStudentOptions } from "../../../services/StudentService";
import { getStudentIdCard } from "../../../services/StudentIdCard";
import CustomAsyncDropdown from "../common/NewCustomAsyncSelectDropdown";
import { Skeleton, SkeletonGroup } from "../common/Skeleton/Skeleton.jsx";
import { silenceFor } from "../common/Loader/busy";
import debounce from "lodash/debounce";
import "../Trainer/TrainerDashboard.css";
import "../Student/StudentProfile/StudentProfile.css";
import "./StudentIdCard.css";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";

export default function StudentCard() {
  const [student, setStudent] = useState(null);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const hasShownToast = useRef(false);
  const initialized = useRef(false);
  const { hasPermission } = useAuth();
  const cardRef = useRef();
  const [isExporting, setIsExporting] = useState(false);

  const [isAdmin, setIsAdmin] = useState(false);
  const [selectedStudentEmail, setSelectedStudentEmail] = useState(null);
  const [selectedStudentName, setSelectedStudentName] = useState("");
  const [selectedStudentValue, setSelectedStudentValue] = useState(null);
  const [studentOptions, setStudentOptions] = useState([]);
  const [studentOptionsLoading, setStudentOptionsLoading] = useState(false);
  const [studentSearchTerm, setStudentSearchTerm] = useState("");
  const [studentPage, setStudentPage] = useState(1);
  const [hasMoreStudents, setHasMoreStudents] = useState(true);
  const isFetchingStudents = useRef(false);
  const [hasLoadedInitial, setHasLoadedInitial] = useState(false);

  useEffect(() => {
    if (!hasPermission("student-id-card", "view", "lms")) {
      if (!hasShownToast.current) {
        toast.error("You do not have permission to view student ID cards.");
        navigate("/");
        hasShownToast.current = true;
      }
    }
  }, [hasPermission, navigate]);

  const checkIfAdmin = (userData) => userData && userData.isSuperAdmin;

  const fetchStudents = useCallback(async (search = "", pageNum = 1) => {
    try {
      isFetchingStudents.current = true;
      // Names only (server skips the join): the picker reads name and email.
      const response = await getStudentOptions(20, pageNum, search);
      const students = response.data || [];
      return { students, hasMore: students.length === 20 };
    } catch (err) {
      return { students: [], hasMore: false };
    } finally {
      isFetchingStudents.current = false;
    }
  }, []);

  const loadStudents = useCallback(async (search = "", pageNum = 1, append = false) => {
    setStudentOptionsLoading(true);
    const { students, hasMore } = await fetchStudents(search, pageNum);
    const newOptions = students.map((s) => ({
      value: s.email,
      label: `${s.name} | ${s.email}`,
      student: s,
    }));
    if (append) setStudentOptions((prev) => [...prev, ...newOptions]);
    else setStudentOptions(newOptions);
    setHasMoreStudents(hasMore);
    setStudentOptionsLoading(false);
    return { students, hasMore };
  }, [fetchStudents]);

  const handleLoadMore = useCallback(async () => {
    if (!hasMoreStudents || studentOptionsLoading || isFetchingStudents.current) return;
    const nextPage = studentPage + 1;
    setStudentPage(nextPage);
    await loadStudents(studentSearchTerm, nextPage, true);
  }, [hasMoreStudents, studentOptionsLoading, studentPage, studentSearchTerm, loadStudents]);

  const handleStudentSearch = useMemo(
    () => debounce(async (inputValue) => {
      setStudentSearchTerm(inputValue);
      setStudentPage(1);
      await loadStudents(inputValue, 1, false);
    }, 700),
    [loadStudents],
  );

  const handleRefresh = useCallback(async () => {
    if (!isAdmin) return;
    setStudentSearchTerm("");
    setStudentPage(1);
    setHasLoadedInitial(false);
    await loadStudents("", 1, false);
    setHasLoadedInitial(true);
  }, [isAdmin, loadStudents]);

  const handleStudentMenuOpen = useCallback(async () => {
    if (!hasLoadedInitial && studentOptions.length === 0 && !studentOptionsLoading) {
      await loadStudents("", 1, false);
      setHasLoadedInitial(true);
    }
  }, [hasLoadedInitial, studentOptions.length, studentOptionsLoading, loadStudents]);

  const fetchIdCardData = useCallback(async (email = null) => {
    try {
      // The card shows its own skeleton, so the global overlay stays down.
      silenceFor();
      setLoading(true);
      setError(null);
      const response = await getStudentIdCard(email);
      const { student: studentArray, courses: coursesData } = response.data;
      const firstStudent = studentArray?.length > 0 ? studentArray[0] : null;
      if (firstStudent) {
        setStudent(firstStudent);
        setCourses(coursesData || []);
      } else {
        setError(email ? "No ID card data found for this student." : "No ID card data found.");
        setStudent(null);
        setCourses([]);
      }
    } catch (err) {
      setError("Failed to load ID card data. Please try again.");
      setStudent(null);
      setCourses([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleStudentSelection = async (selectedOptions) => {
    const selectedOption = selectedOptions?.length > 0 ? selectedOptions[0] : null;
    if (!selectedOption) {
      setSelectedStudentEmail(null);
      setSelectedStudentName("");
      setSelectedStudentValue(null);
      setStudent(null);
      setCourses([]);
      setError(null);
      setStudentSearchTerm("");
      return;
    }
    const { value: email, label: fullLabel } = selectedOption;
    setSelectedStudentEmail(email);
    setSelectedStudentName(fullLabel);
    setSelectedStudentValue(selectedOption);
    await fetchIdCardData(email);
  };

  const handleRetry = async () => {
    if (isAdmin && selectedStudentEmail) await fetchIdCardData(selectedStudentEmail);
    else await fetchIdCardData();
  };

  const fetchStudentDataForCurrentStudent = useCallback(async () => {
    if (initialized.current) return;
    initialized.current = true;
    try {
      silenceFor();
      setLoading(true);
      const currentUser = await getMe();
      const userData = currentUser.data;
      const adminStatus = checkIfAdmin(userData);
      setIsAdmin(adminStatus);
      if (!adminStatus) await fetchIdCardData();
      else setLoading(false);
    } catch (err) {
      setError("Failed to load user data");
      setLoading(false);
    }
  }, [fetchIdCardData]);

  useEffect(() => {
    fetchStudentDataForCurrentStudent();
  }, [fetchStudentDataForCurrentStudent]);

  const handleDownloadPDF = async () => {
    setIsExporting(true);
    await new Promise((resolve) => setTimeout(resolve, 100));
    const input = cardRef.current;
    if (!input) { setIsExporting(false); return; }
    const canvas = await html2canvas(input, { scale: 2, useCORS: true, backgroundColor: "#fff" });
    const imgData = canvas.toDataURL("image/png");
    const pdf = new jsPDF({ orientation: "landscape", unit: "px", format: [input.offsetWidth, input.offsetHeight] });
    pdf.addImage(imgData, "PNG", 0, 0, input.offsetWidth, input.offsetHeight);
    pdf.save("student-id-card.pdf");
    setIsExporting(false);
  };

  const getDepartmentNames = () => {
    if (!student?.departmentIds?.length) return "N/A";
    return student.departmentIds.map(d => d.name).join(", ");
  };

  const getBranchNames = () => {
    if (!student?.preferredBranchId?.length) return "N/A";
    return student.preferredBranchId.map(b => b.name).join(", ");
  };

  const showDropdown = isAdmin && selectedStudentEmail;
  const formattedStudentId = student?.studentIdNumber || "NA";
  const courseNames = courses.map((c) => c.name).join(", ") || "No courses enrolled";
  const avatarUrl = `https://ui-avatars.com/api/?name=${encodeURIComponent(
    (student?.name || "S").split(" ").slice(0, 2).join("+")
  )}&background=F26A1B&color=fff&size=256&bold=true`;

  /* One dropdown, rendered into the gate before a student is picked
     and into the picker strip afterwards. */
  const studentPicker = (
    <CustomAsyncDropdown
      options={studentOptions}
      value={selectedStudentValue ? [selectedStudentValue] : []}
      onChange={handleStudentSelection}
      placeholder="Search by name or email…"
      onInputChange={handleStudentSearch}
      onMenuScrollToBottom={handleLoadMore}
      onMenuOpen={handleStudentMenuOpen}
      onRefresh={handleRefresh}
      isLoading={studentOptionsLoading}
      isSearchable={true}
      isMulti={false}
    />
  );

  /* The card's own shape, shimmering, in the .sid-card frame: the head,
     the four figures in the strip, then the photo beside the name and
     fields. The same skeleton stands in for the card in both frames. */
  const cardSkeleton = (
    <SkeletonGroup label="Loading ID card" className="sid-card lms-skel-stack">
      <div className="lms-skel-row">
        <Skeleton shape="mark" />
        <div className="lms-skel-stack lms-skel-grow">
          <Skeleton shape="title" width="40" />
          <Skeleton width="55" />
        </div>
      </div>
      <div className="sid-strip">
        {[0, 1, 2, 3].map((i) => (
          <div className="sid-stat lms-skel-stack" key={i}>
            <Skeleton shape="title" width="55" />
            <Skeleton width="40" />
          </div>
        ))}
      </div>
      <div className="lms-skel-row">
        <Skeleton shape="figure" width="25" />
        <div className="lms-skel-stack lms-skel-grow">
          <Skeleton shape="title" width="55" />
          <Skeleton width="70" />
          <Skeleton width="85" />
          <Skeleton width="55" />
        </div>
      </div>
    </SkeletonGroup>
  );

  /* ─── Loading, before anything is known ─── */
  if (loading && !isAdmin) {
    return (
      <div className="dashboard-wrapper">
        <div className="sid">
          <div className="sid-wrap">
            <div className="sid-head">
              <div>
                <div className="sid-eyebrow">Identity document</div>
                <h1 className="sid-title">Student ID card</h1>
              </div>
            </div>
            {cardSkeleton}
          </div>
        </div>
      </div>
    );
  }

  /* ─── Staff with nobody selected ───
     The same gate /student-profile draws, class for class — it uses the
     sp-gate block from StudentProfile.css rather than a copy of it, so
     the two cannot drift apart. */
  if (isAdmin && !selectedStudentEmail) {
    return (
      <div className="dashboard-wrapper">
        <div className="td-page">
          <div className="sp-gate">
            <span className="sp-gate-mark">
              <User size={22} aria-hidden="true" />
            </span>
            <h1 className="sp-gate-title">Pick a student</h1>
            <p className="sp-gate-text">
              Choose a student to preview and download their ID card.
            </p>
            <div className="sp-gate-field">{studentPicker}</div>
            <span className="sp-gate-tip">
              <FileText size={14} aria-hidden="true" />
              Search by name, email or phone number.
            </span>
          </div>
        </div>
      </div>
    );
  }

  /* =============================================================
     THE CARD

     Both views show the same document — an ID card does not change
     shape depending on who is holding it. Only the frame around it
     differs: staff get the picker strip above it, a student gets the
     page on its own.
     ============================================================= */
  const idCard = (
    <div className="sid-card" ref={cardRef}>
      <div className="sid-watermark" aria-hidden="true">SM</div>

      <div className="sid-card-head">
        <div className="sid-brand">
          <img className="sid-logo" src={Logo} alt="SevenMentor" />
          <div>
            <div className="sid-brand-name">SevenMentor</div>
            <div className="sid-brand-where">
              Mumbai · Pune · Pimpri-Chinchwad · Hadapsar · Nanded
            </div>
          </div>
        </div>
        <span className="sid-kind">STUDENT ID</span>
      </div>

      <div className="sid-strip">
        <div className="sid-stat">
          <span className="sid-stat-icon">
            <CreditCard size={18} aria-hidden="true" />
          </span>
          <span className="sid-stat-value">{formattedStudentId}</span>
          <span className="sid-stat-label">Student ID</span>
        </div>
        <div className="sid-stat">
          <span className="sid-stat-icon">
            <BookOpen size={18} aria-hidden="true" />
          </span>
          <span className="sid-stat-value">{courses.length || 0}</span>
          <span className="sid-stat-label">Courses</span>
        </div>
        <div className="sid-stat">
          <span className="sid-stat-icon">
            <Award size={18} aria-hidden="true" />
          </span>
          <span className="sid-stat-value">{getDepartmentNames()}</span>
          <span className="sid-stat-label">Department</span>
        </div>
        <div className="sid-stat">
          <span className="sid-stat-icon">
            <Building2 size={18} aria-hidden="true" />
          </span>
          <span className="sid-stat-value">{getBranchNames()}</span>
          <span className="sid-stat-label">Branch</span>
        </div>
      </div>

      <div className="sid-body">
        <div className="sid-photo-col">
          <div className="sid-photo">
            <img src={avatarUrl} alt="" />
          </div>
          <span className="sid-number">{formattedStudentId}</span>
        </div>

        <div>
          <div className="sid-name">{student?.name || "N/A"}</div>
          <div className="sid-course">{courseNames}</div>

          <dl className="sid-fields">
            <div>
              <dt>Email</dt>
              <dd>{student?.email || "N/A"}</dd>
            </div>
            <div>
              <dt>Contact</dt>
              <dd>{student?.phone || "N/A"}</dd>
            </div>
            <div>
              <dt>Department</dt>
              <dd>{getDepartmentNames()}</dd>
            </div>
            <div>
              <dt>Branch</dt>
              <dd>{getBranchNames()}</dd>
            </div>
          </dl>

          <span className="sid-valid">Valid · SevenMentor Learning Hub</span>
        </div>
      </div>

      <div className="sid-card-foot">
        <span className="sid-foot-note">
          This card is the property of SevenMentor and must be returned on demand.
        </span>
        <span className="sid-foot-org">SevenMentor Pvt. Ltd.</span>
      </div>
    </div>
  );

  const downloadButton = student && (
    <button
      type="button"
      className="sid-btn"
      onClick={handleDownloadPDF}
      disabled={isExporting}
    >
      <Download size={15} aria-hidden="true" />
      {isExporting ? "Exporting…" : "Download PDF"}
    </button>
  );

  /* The three things that can be shown instead of the card. */
  const cardOrState = error ? (
    <div className="sid-state sid-state--bad">
      <span className="sid-state-mark">
        <AlertCircle size={22} aria-hidden="true" />
      </span>
      <h2 className="sid-state-title">Unable to load the ID card</h2>
      <p className="sid-state-text">{error}</p>
      <button type="button" className="sid-btn sid-btn--ghost" onClick={handleRetry}>
        <RefreshCw size={14} aria-hidden="true" />
        Try again
      </button>
    </div>
  ) : loading ? (
    cardSkeleton
  ) : !student ? (
    <div className="sid-state">
      <span className="sid-state-mark">
        <User size={22} aria-hidden="true" />
      </span>
      <h2 className="sid-state-title">No ID card available</h2>
      <p className="sid-state-text">
        {showDropdown
          ? `There is no ID card record for ${selectedStudentName || "this student"}.`
          : "There is no ID card record for your account yet. Your coordinator can issue one."}
      </p>
    </div>
  ) : (
    idCard
  );

  /* ─── Staff frame ─── */
  if (isAdmin) {
    return (
      <div className="dashboard-wrapper">
        <div className="td-page">
          <div className="sid-picker">
            <div className="sid-picker-field">
              <label className="sid-picker-label">Viewing student</label>
              {studentPicker}
            </div>
            {downloadButton}
          </div>

          {cardOrState}
        </div>
      </div>
    );
  }

  /* ─── Student frame ─── */
  return (
    <div className="dashboard-wrapper">
      <div className="sid">
        <div className="sid-wrap">
          <div className="sid-head">
            <div>
              <div className="sid-eyebrow">Identity document</div>
              <h1 className="sid-title">Student ID card</h1>
            </div>
            {downloadButton}
          </div>

          {cardOrState}
        </div>
      </div>
    </div>
  );
}
